import debounce from "lodash/debounce";
import {
  Building,
  Download,
  FileQuestion,
  Loader2,
  Plus,
  Search,
  UserCheck,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CasesAPI, {
  AREA_OF_FRAUD_LABEL_MAP,
  AreaOfFraud,
  CaseStatus,
} from "../../../api/cases";
import { useAppDispatch, useAppSelector } from "../../../app/store";
import ButtonV2 from "../../../components/ui/button/v2";
import CaseStatusBadge, {
  STATUS_OPTIONS,
  textMap,
} from "../../../components/ui/casestatusbadge/CaseStatusBadge";
import {
  DateRange,
  DateRangeQuickSelect,
  DateRangeSelect,
} from "../../../components/ui/daterangeselect/DateRangeSelect";
import { MultiSelect } from "../../../components/ui/multiselect/MultiSelect";
import { OptionNameValuePair } from "../../../lib/interfaces/types";
import {
  addDaysToToday,
  formatDateForInput,
  getCurrentDate,
  getFirstDayOfCurrentMonth,
} from "../../../lib/utils";
import { Can } from "../../auth/components/Can";
import { Permission, useAbility } from "../../auth/logic/rbac";
import {
  getZoneValues,
  isCreditAccountStakeholdersRevampEnabled,
} from "../../configs/logic/Slice";
import {
  getCaseFilters,
  getSearchStrings,
  getSelectedAreaOfFraud,
  getSelectedInvestigatorValues,
  getSelectedReferralDateRange,
  getSelectedStatus,
  getSelectedZoneValues,
  setAreaOfFraudFilter,
  setInvestigatorFilter,
  setReferralDateRangeFilter,
  setSearchStrings,
  setStatusFilter,
  setZoneFilter,
} from "../logic/CaseFiltersSlice";
import { buildCaseFilterParams, fetchCases } from "../logic/Thunks";
import { getInvestigators } from "../../users/logic/slice";
import { showErrorToast } from "../../../../src/lib/toast";

interface CaseFilterSectionProps {
  onCreateCase: () => void;
}

const CaseFilterSection: React.FC<CaseFilterSectionProps> = ({
  onCreateCase,
}) => {
  const dispatch = useAppDispatch();
  const { can } = useAbility();

  const canListCases = can(Permission.LIST_CASES);

  const zoneFilterValues = useAppSelector(getZoneValues);
  const isCreditRevampEnabled = useAppSelector(
    isCreditAccountStakeholdersRevampEnabled,
  );

  const searchStrings = useAppSelector(getSearchStrings);
  const [searchString, setSearchString] = useState<string>(
    searchStrings.join(", "),
  );
  
  const [isDownloading, setIsDownloading] = useState(false);

  const zoneOptions: OptionNameValuePair<string>[] | undefined =
    zoneFilterValues?.map((zone) => ({ name: zone, value: zone }));
  const selectedZoneValues = useAppSelector(getSelectedZoneValues);
  const updateZoneFilterValues = (values: string[]) => {
    dispatch(setZoneFilter(values));
  };

  const investigators = useAppSelector(getInvestigators);

  const caseFilters = useAppSelector(getCaseFilters);
  const investigatorOptions: OptionNameValuePair<string>[] = investigators.map(
    (investigator) => ({
      name: investigator.user_name,
      value: investigator.user_id,
    }),
  );
  const selectedInvestigatorValues = useAppSelector(
    getSelectedInvestigatorValues,
  );
  const updateInvestigatorFilterValues = (values: string[]) => {
    dispatch(setInvestigatorFilter(values));
  };

  const selectedReferralDateRange = useAppSelector(getSelectedReferralDateRange);
  const updateSelectedReferralDateRange = (dateRange: DateRange) => {
    dispatch(setReferralDateRangeFilter(dateRange));
  };

  const current_date = getCurrentDate();
  const dateQuickSelectOptions: DateRangeQuickSelect[] = [
    {
      name: "Last 7 days",
      dateRange: {
        from: formatDateForInput(addDaysToToday(-7)),
        to: formatDateForInput(current_date),
      },
    },
    {
      name: "Last 30 days",
      dateRange: {
        from: formatDateForInput(addDaysToToday(-30)),
        to: formatDateForInput(current_date),
      },
    },
    {
      name: "Last 90 days",
      dateRange: {
        from: formatDateForInput(addDaysToToday(-90)),
        to: formatDateForInput(current_date),
      },
    },
    {
      name: "This month",
      dateRange: {
        from: formatDateForInput(getFirstDayOfCurrentMonth()),
        to: formatDateForInput(current_date),
      },
    },
  ];
  const dateRangeDefaultSelection: DateRange = {
    from: formatDateForInput(addDaysToToday(-30)),
    to: formatDateForInput(getCurrentDate()),
  };

  const statusOptions: OptionNameValuePair<CaseStatus>[] = STATUS_OPTIONS.map(
    (status) => ({
      name: textMap[status],
      display_component: <CaseStatusBadge status={status} />,
      value: status,
    }),
  );

  const selectedStatuses = useAppSelector(getSelectedStatus);
  const updateSelectedStatuses = (values: CaseStatus[]) => {
    dispatch(setStatusFilter(values));
  };

  const areaOfFraudOptions: OptionNameValuePair<AreaOfFraud>[] = useMemo(
    () =>
      (Object.values(AreaOfFraud) as AreaOfFraud[]).map((area) => ({
        name: AREA_OF_FRAUD_LABEL_MAP[area],
        value: area,
      })),
    [],
  );

  const selectedAreaOfFraud = useAppSelector(getSelectedAreaOfFraud);
  const updateSelectedAreaOfFraud = (values: AreaOfFraud[]) => {
    dispatch(setAreaOfFraudFilter(values));
  };

  const abortRef = useRef(() => {});

  const fetchData = useCallback(() => {
    if (abortRef.current) {
      abortRef.current();
    }
    const a = dispatch(fetchCases());
    abortRef.current = () => a.abort();
  }, [dispatch]);

  const searchBySearchString = async () => {
    if (searchString && searchString.length > 0) {
      const searchStrings = searchString.split(",").map((str) => str.trim());
      dispatch(setSearchStrings(searchStrings));
      fetchData();
    }
  };

  const clearSearchString = () => {
    dispatch(setSearchStrings([]));
    setSearchString("");
    fetchData();
  };

  const downloadCases = async () => {
    if (isDownloading) return;

    try {
      setIsDownloading(true);
      const params = buildCaseFilterParams(caseFilters);
      const blob = isCreditRevampEnabled
        ? await CasesAPI.download(params)
        : await CasesAPI.downloadLegacy(params);

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = "Cases.xlsx";

      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      showErrorToast("Failed to download cases")
    } finally {
      setIsDownloading(false);
    }
  };

  const fetchDataWithDebounce = useMemo(
    () => debounce(fetchData, 1000),
    [fetchData],
  );

  const didMount = useRef(false);
  useEffect(() => {
    if (didMount.current && canListCases) {
      fetchDataWithDebounce();
    } else didMount.current = true;
  }, [
    selectedReferralDateRange,
    selectedZoneValues,
    selectedInvestigatorValues,
    selectedStatuses,
    selectedAreaOfFraud,
    canListCases,
    fetchDataWithDebounce,
  ]);

  useEffect(() => {
    if (canListCases) {
      dispatch(fetchCases());
    }
  }, [canListCases, fetchCases]);

  return (
    <div className="w-full">
      <div className="flex flex-col items-start justify-between gap-3">
        <div className="relative w-full sm:w-[336px]">
          <input
            placeholder="Search cases by UIN(s), comma-separated"
            className="h-9 w-full rounded-md border border-gray-200 bg-white pl-9 text-sm text-[#0A0A0A] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
            type="text"
            value={searchString}
            onChange={(event) => {
              setSearchString(event.target.value);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                searchBySearchString();
              }
            }}
            disabled={!canListCases}
          />
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          {searchString && searchString.length > 0 && (
            <button
              type="button"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 hover:bg-gray-50"
              onClick={clearSearchString}
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className="w-full flex flex-wrap items-center justify-between gap-2">
          <Can perform={Permission.LIST_CASES} variant="hide">
            <div className="flex flex-wrap items-center justify-start gap-2">
              <DateRangeSelect
                name={<span className="inline-flex items-center h-5 px-2 py-0.5 rounded-md bg-[#F5F5F5] text-xs font-medium text-[#737373]">Referral</span>}
                selectedRange={selectedReferralDateRange}
                defaultSelection={dateRangeDefaultSelection}
                quickSelectOptions={dateQuickSelectOptions}
                onChange={(selection) => {
                  updateSelectedReferralDateRange(selection);
                }}
                rootDateRangeSelectClassName="h-9 inline-flex"
              />

              {zoneOptions && (
                <MultiSelect
                  name="Zone"
                  IconComponent={Building}
                  options={zoneOptions}
                  selectedValues={selectedZoneValues}
                  onSelectionChange={updateZoneFilterValues}
                  buttonClassName="h-9"
                />
              )}
              <Can perform={Permission.CAN_FILTER_INVESTIGATOR} variant="hide">
                {investigatorOptions && (
                  <MultiSelect
                    name="Investigator"
                    IconComponent={UserCheck}
                    options={investigatorOptions}
                    selectedValues={selectedInvestigatorValues}
                    onSelectionChange={updateInvestigatorFilterValues}
                    buttonClassName="h-9"
                  />
                )}
              </Can>
              {statusOptions && (
                <MultiSelect
                  name="Status"
                  IconComponent={FileQuestion}
                  options={statusOptions}
                  selectedValues={selectedStatuses}
                  onSelectionChange={updateSelectedStatuses}
                  dropdownWidthClassName="w-[17.5rem]"
                  buttonClassName="h-9"
                />
              )}

              {areaOfFraudOptions && (
                <MultiSelect
                  name="Area of fraud"
                  IconComponent={FileQuestion}
                  options={areaOfFraudOptions}
                  selectedValues={selectedAreaOfFraud}
                  onSelectionChange={updateSelectedAreaOfFraud}
                  dropdownWidthClassName="w-[17.5rem]"
                  buttonClassName="h-9"
                />
              )}
            </div>
          </Can>

          <div className="flex flex-wrap items-center justify-end gap-2">
            <Can perform={Permission.DOWNLOAD_CASES} variant="hide">
              <button
                type="button"
                className="h-9 w-9 rounded-md border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 inline-flex items-center justify-center disabled:cursor-not-allowed"
                onClick={downloadCases}
                aria-label="Download"
                disabled={isDownloading}
              >
                {isDownloading ? (<Loader2 className="h-4 w-4 animate-spin" />) : (<Download className="h-4 w-4" />)}
              </button>
            </Can>
            <ButtonV2
              variant="primary"
              size="md"
              prefix={<Plus className="h-3.5 w-3.5 text-white" />}
              onClick={onCreateCase}
              className="h-9"
            >
              Create Case
            </ButtonV2>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CaseFilterSection;
